import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Subject, takeUntil, timer, fromEvent, startWith, switchMap, EMPTY } from 'rxjs';
import { take } from 'rxjs/operators';

import { API_BASE } from './oidc.config';

/** How often to poll the IdP for session liveness, in milliseconds. */
const POLL_INTERVAL_MS = 60_000;

interface StatusResponse {
  active: boolean;
  sid: string;
  idleSeconds: number;
}

export interface PollState {
  /** When the most recent poll completed (success or error). Null before the first poll. */
  lastPolledAt: Date | null;
  /** Most recent successful response. Null until at least one success. */
  lastResult: { active: boolean; idleSeconds: number; sid: string } | null;
  /** HTTP status of the last poll. Null on success, populated on error. */
  lastErrorStatus: number | null;
  /** Number of consecutive poll failures since the last success. Resets on success. */
  consecutiveFailures: number;
}

const INITIAL_STATE: PollState = {
  lastPolledAt: null,
  lastResult: null,
  lastErrorStatus: null,
  consecutiveFailures: 0,
};

/**
 * Polls `GET /api/AvalonIdentitySessions/Status` every 60 seconds to detect
 * cross-client logout (user signed out from AIM elsewhere → BCL fan-out
 * flips OidcSsoSession.revoked → next poll returns active=false → this
 * service triggers RP-initiated logout to bring the SPA in sync).
 *
 * Design notes:
 *
 * - **Visibility-aware:** pauses while `document.hidden` is true (tab in
 *   background), resumes on `visibilitychange`. Avoids polling burn while
 *   the user has the tab backgrounded for an extended period.
 *
 * - **Read-only on the server:** the Status endpoint does NOT slide the
 *   session window. Polling is "is my session still alive?" — it must NOT
 *   keep the session alive, otherwise idle timeout never fires.
 *
 * - **`active=false` triggers logout:** library's `logoff()` clears
 *   in-memory tokens and redirects to `/connect/endsession`. If the IdP
 *   already revoked the session (BCL path), the endsession redirect is
 *   essentially a no-op server-side — but the FE still navigates through
 *   `/auth/logout` to land cleanly.
 *
 * - **`401` triggers logout:** the access token was rejected. The reason
 *   is essentially the same as `active=false` (revoked or expired session)
 *   — bounce the user.
 *
 * - **Other errors (5xx, network) are tolerated:** transient. Tracked via
 *   `consecutiveFailures` for UI display; only triggers logout if you
 *   later choose to add a "after N consecutive failures" threshold.
 */
@Injectable({providedIn: 'root'})
export class SessionPollService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly oidc = inject(OidcSecurityService);

  private destroy$ = new Subject<void>();
  private started = false;

  readonly state = signal<PollState>(INITIAL_STATE);

  /**
   * Start the polling loop. Idempotent — calling start() repeatedly is
   * a no-op. Call once when the user becomes authenticated.
   */
  public start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    // 1. Create a stream that emits whenever the document visibility changes.
    // We also startWith(null) so that the stream triggers immediately on initialization.
    fromEvent(document, 'visibilitychange')
      .pipe(
        // startWith handles the initial poll setup right away
        startWith(null),
        switchMap(() => {
          // If the document is hidden, we don't want to poll at all.
          if (document.hidden) {
            return EMPTY; // Emits nothing and effectively pauses polling
          }

          // When the document becomes visible (or at startup):
          // timer(0, interval) fires IMMEDIATELY (0ms), then every X ms.
          // Because of switchMap, if a visibility change happens, any previous
          // active timer is cancelled/discarded, resetting the countdown.
          return timer(0, POLL_INTERVAL_MS);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.poll();
      });
  }

  /**
   * Stop the polling loop and reset state. Call on sign-out.
   */
  stop(): void {
    this.destroy$.next();
    this.started = false;
    this.state.set(INITIAL_STATE);
  }

  /**
   * Manually trigger one poll right now (for diagnostic UI buttons).
   */
  pollNow(): void {
    this.poll();
  }

  private poll(): void {
    this.http
      .get<StatusResponse>(`${API_BASE}/api/AvalonIdentitySessions/Status`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.state.set({
            lastPolledAt: new Date(),
            lastResult: {active: res.active, idleSeconds: res.idleSeconds, sid: res.sid},
            lastErrorStatus: null,
            consecutiveFailures: 0,
          });

          if (!res.active) {
            this.oidc.logoff().pipe(
              take(1),
              takeUntil(this.destroy$),
            ).subscribe();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.state.update((s) => ({
            ...s,
            lastPolledAt: new Date(),
            lastErrorStatus: err.status || 0,
            consecutiveFailures: s.consecutiveFailures + 1,
          }));

          if (err.status === 401) {
            this.oidc.logoff().pipe(
              take(1),
              takeUntil(this.destroy$),
            ).subscribe();
          }
        },
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();
    this.started = false;
   this.state.set(INITIAL_STATE);
  }
}
